// ============================================
// @gs/auth — AuthProvider (MSAL React wrapper)
// ============================================
//
// Stratégie : TOUT en mode redirect (loginRedirect / logoutRedirect).
// On n'utilise JAMAIS loginPopup/logoutPopup.
//
// Flux :
//   1. handleRedirectPromise() — capture le retour de toute redirection
//   2. Si déjà authentifié (cache MSAL) → charger l'user directement
//   3. Sinon → ssoSilent() pour tenter une connexion transparente
//   4. Si ssoSilent échoue → afficher l'écran de login
//
// Résolution du rôle (ordre de priorité) :
//   1. /api/auth/me (BD) → rôle fiable depuis DIM_UserAppRoles
//   2. JWT groups claims → fallback si pas de backend
//   3. defaultRole → dernier recours
// ============================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { acquireJwtForOwnBackend } from './acquireJwtForOwnBackend';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { getConfig } from '@gs/config/browser';
import { AuthContext, type GsUser, GsRole, type AuthState } from './index';

export interface RoleMapping {
  groupId: string;
  role: GsRole;
}

export interface AuthProviderProps {
  children: React.ReactNode;
  roleMappings?: RoleMapping[];
  defaultRole?: GsRole;
  /** Clé d'app pour lire le rôle depuis /api/auth/me (ex: 'inventaire') */
  appKey?: string;
}

// ─── Flag logout explicite ────────────────────────────────────────────────────
const LOGOUT_FLAG = 'gs_auth_logged_out';
function setLogoutFlag()   { try { sessionStorage.setItem(LOGOUT_FLAG, '1');   } catch {} }
function clearLogoutFlag() { try { sessionStorage.removeItem(LOGOUT_FLAG);     } catch {} }
function hasLogoutFlag()   { try { return sessionStorage.getItem(LOGOUT_FLAG) === '1'; } catch { return false; } }

// ─── Détection : l'app a-t-elle un backend ? ─────────────────────────────────
function hasBackend(apiUrl: string): boolean {
  try {
    if (!apiUrl) return false;
    const apiOrigin = new URL(apiUrl).origin;
    const appOrigin = window.location.origin;
    return apiOrigin !== appOrigin;
  } catch {
    return false;
  }
}

// ─── Singleton MSAL ───────────────────────────────────────────────────────────
let msalInstance: PublicClientApplication | null = null;

function createMsalInstance(): PublicClientApplication {
  const config = getConfig();
  const msalConfig: Configuration = {
    auth: {
      clientId:               config.auth.clientId,
      authority:              `https://login.microsoftonline.com/${config.auth.tenantId}`,
      redirectUri:            config.auth.redirectUri,
      postLogoutRedirectUri:  config.auth.redirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation:          'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback:   () => {},
        piiLoggingEnabled: false,
      },
    },
  };

  const instance = new PublicClientApplication(msalConfig);
  instance.initialize();
  return instance;
}

function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) msalInstance = createMsalInstance();
  return msalInstance;
}

// ─── Résolution du rôle depuis les claims JWT (fallback) ─────────────────────
function roleFromJwtGroups(
  account: AccountInfo,
  roleMappings: RoleMapping[],
  defaultRole: GsRole
): GsRole {
  const groups = (account.idTokenClaims?.groups as string[] | undefined) ?? [];
  for (const mapping of roleMappings) {
    if (groups.includes(mapping.groupId)) return mapping.role;
  }
  return defaultRole;
}

// ─── Composant interne ────────────────────────────────────────────────────────
function AuthProviderInner({
  children,
  roleMappings = [],
  defaultRole = GsRole.UTILISATEUR,
  appKey,
}: AuthProviderProps) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [user, setUser]                       = useState<GsUser | null>(null);
  const [isLoading, setIsLoading]             = useState(true);
  const [redirectHandled, setRedirectHandled] = useState(false);

  const config  = getConfig();
  const scopes  = config.auth.scopes;
  const backend = hasBackend(config.apiUrl);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Charge l'utilisateur dans le contexte.
   *
   * Résolution du rôle :
   *   1. Si backend disponible → /api/auth/me → lire le rôle depuis DIM_UserAppRoles (BD)
   *      C'est la source de vérité. Plus fiable que les claims JWT qui nécessitent
   *      une configuration Azure AD spécifique (groupMembershipClaims).
   *   2. Fallback → JWT groups claims → résolution via ROLE_MAPPINGS
   *   3. Dernier recours → defaultRole (UTILISATEUR)
   */
  const loadUser = useCallback(
    async (account: AccountInfo) => {
      try {
        setIsLoading(true);

        // Rôle JWT = fallback si l'API est indisponible
        const jwtRole = roleFromJwtGroups(account, roleMappings, defaultRole);

        let resolvedRole = jwtRole;
        let allowedServicePoints: string[] = [];

        if (backend) {
          try {
            const apiToken = await acquireJwtForOwnBackend(
              instance,
              account,
              scopes,
              config.auth.clientId
            );
            const response = await fetch(`${config.apiUrl}/api/auth/me`, {
              headers: { Authorization: `Bearer ${apiToken}` },
            });

            if (response.ok) {
              const data = await response.json();

              // ── Lire le rôle depuis la BD (source de vérité) ──────────────
              // /api/auth/me retourne appRoles: [{ appKey, role }]
              // On cherche le rôle pour l'app courante (appKey) ou le premier disponible
              const appRoles = (data.appRoles ?? []) as { appKey: string; role: string }[];
              const match = appKey
                ? appRoles.find((r) => r.appKey === appKey)
                : appRoles[0];

              if (match?.role) {
                // Valider que le rôle BD est un GsRole valide
                const roleValues = Object.values(GsRole) as string[];
                if (roleValues.includes(match.role)) {
                  resolvedRole = match.role as GsRole;
                }
              }
              // Si pas de rôle en BD → garder le rôle JWT (jwtRole)

              allowedServicePoints = data.allowedServicePoints ?? [];
            }
          } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
              console.warn('@gs/auth: interaction requise pour acquireTokenSilent');
            } else {
              console.warn('@gs/auth: API auth/me indisponible — rôle JWT utilisé', e);
            }
            // Fallback silencieux sur le rôle JWT
          }
        }

        clearLogoutFlag();
        setUser({
          id:                   account.localAccountId,
          name:                 account.name ?? account.username,
          email:                account.username,
          role:                 resolvedRole,
          allowedServicePoints,
          initials:             getInitials(account.name ?? account.username),
        });
      } catch (err) {
        console.error('@gs/auth: loadUser error', err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, scopes, config.apiUrl, appKey, backend]
  );

  const trySsoSilent = useCallback(async (): Promise<boolean> => {
    if (hasLogoutFlag()) return false;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const loginHint = urlParams.get('gs_hint') ?? undefined;

      const result = await instance.ssoSilent({
        scopes,
        prompt: 'none',
        ...(loginHint ? { loginHint } : {}),
      });
      if (result?.account) {
        await loadUser(result.account);
        return true;
      }
    } catch (e) {
      if (!(e instanceof BrowserAuthError) && !(e instanceof InteractionRequiredAuthError)) {
        console.warn('@gs/auth: ssoSilent inattendu', e);
      }
    }
    return false;
  }, [instance, scopes, loadUser]);

  // ── Étape 1 : Traiter le retour de redirection ────────────────────────────
  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((result) => {
        if (result?.account) void loadUser(result.account);
      })
      .catch((err) => {
        console.error('@gs/auth: handleRedirectPromise error', err);
      })
      .finally(() => {
        setRedirectHandled(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Étape 2 : Décider quoi faire après le redirect ────────────────────────
  useEffect(() => {
    if (!redirectHandled) return;
    if (user) return;

    if (isAuthenticated && accounts.length > 0) {
      void loadUser(accounts[0]);
    } else {
      trySsoSilent().then((success) => {
        if (!success) setIsLoading(false);
      });
    }
  }, [redirectHandled, isAuthenticated, accounts, user, loadUser, trySsoSilent]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const login = useCallback(async () => {
    clearLogoutFlag();
    try {
      await instance.clearCache();
    } catch { /* ignorer */ }
    try {
      await instance.loginRedirect({ scopes });
    } catch (err) {
      console.error('@gs/auth: loginRedirect error', err);
    }
  }, [instance, scopes]);

  const logout = useCallback(async () => {
    setUser(null);
    setLogoutFlag();
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: config.auth.redirectUri,
      });
    } catch (err) {
      console.error('@gs/auth: logoutRedirect error', err);
      setIsLoading(false);
    }
  }, [instance, config.auth.redirectUri]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!accounts.length) throw new Error('Aucun compte connecté');
    const cfg = getConfig();
    return acquireJwtForOwnBackend(instance, accounts[0], scopes, cfg.auth.clientId);
  }, [instance, accounts, scopes]);

  const authState: AuthState = useMemo(
    () => ({ user, isLoading, error: null, login, logout, getAccessToken }),
    [user, isLoading, login, logout, getAccessToken]
  );

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────
export function AuthProvider(props: AuthProviderProps) {
  return (
    <MsalProvider instance={getMsalInstance()}>
      <AuthProviderInner {...props} />
    </MsalProvider>
  );
}
